import { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import clsx from 'clsx'
import { motion, MotionConfig, cubicBezier } from 'framer-motion'
import { useAtom, type SetStateAction } from 'jotai'
import { isShowLoadingAtom, isShowVimeoAtom } from '@/stores'
import { IconLogo, Menu } from '@/components'
import { useIsClient } from '@/hooks'
import type { PersonType } from '@/@types/microcms-type'

type MenuButtonProps = {
    isOpen: boolean
    handleToggleMenu: () => void
    styles: string
}

const MenuButton = ({ isOpen, handleToggleMenu, styles }: MenuButtonProps) => {
    const animationVariants = {
        initial: { opacity: 0 },
        animate: { opacity: 1 }
    }

    return (
        <motion.button
            initial="initial"
            animate="animate"
            variants={animationVariants}
            transition={{ delay: 0.3 }}
            onClick={handleToggleMenu}
            className={clsx(
                'duration-300 w-[36px] h-[36px] md:w-[40px] md:h-[40px] lg:w-[3.6vw] lg:h-[3.6vw] relative inline-block group',
                isOpen ? 'bg-v-white/0' : 'bg-v-white/90'
            )}
            aria-label="menu button"
        >
            <span
                className={clsx(
                    styles,
                    'md:group-hover:!rotate-[0deg]',
                    isOpen ? 'rotate-[-45deg]' : 'rotate-[-90deg]'
                )}
            />
            <span
                className={clsx(
                    styles,
                    'md:group-hover:rotate-[0deg] md:group-hover:scale-0',
                    isOpen ? 'rotate-[45deg]' : 'rotate-[0deg]'
                )}
            />
        </motion.button>
    )
}

type CloseLoadingLayerButtonProps = {
    hideShowLoading: () => void
    styles: string
}

const CloseLoadingLayerButton = ({
    hideShowLoading,
    styles
}: CloseLoadingLayerButtonProps) => {
    return (
        <button
            onClick={hideShowLoading}
            className="duration-300 w-[36px] h-[36px] md:w-[40px] md:h-[40px] lg:w-[3.6vw] lg:h-[3.6vw] relative inline-block group bg-v-white md:hover:bg-v-white/60"
            aria-label="menu close button"
        >
            <span className={clsx(styles, 'rotate-[45deg]')} />　
            <span className={clsx(styles, 'rotate-[-45deg]')} />
        </button>
    )
}

interface HeaderLogoProps {
    isHome: boolean
    setIsShowLoading: (value: SetStateAction<boolean>) => void
}

const HeaderLogo = ({ isHome, setIsShowLoading }: HeaderLogoProps) => {
    const logoClasses = `h-auto transition-all duration-[400ms] md:mt-[10px] w-[100px] md:hover:opacity-60 md:w-[${
        isHome ? '18' : '7'
    }vw]`

    const animationVariants = {
        initial: { opacity: 0 },
        animate: { opacity: 1 }
    }

    return (
        <motion.div
            initial="initial"
            animate="animate"
            variants={animationVariants}
        >
            {isHome ? (
                <button
                    onClick={() => setIsShowLoading(false)}
                    title="back button"
                >
                    <IconLogo className={logoClasses} />
                </button>
            ) : (
                <Link href="/" title="header logo" scroll={false}>
                    <IconLogo className={logoClasses} />
                </Link>
            )}
        </motion.div>
    )
}

type HeaderProps = {
    people?: PersonType[]
}

export const Header = ({ people }: HeaderProps) => {
    const router = useRouter()
    const isHome = router.pathname === '/' || router.pathname === '/[lang]'
    const [shufflePeople, setShufflePeople] = useState<PersonType[]>([])
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [isShowLoading, setIsShowLoading] = useAtom(isShowLoadingAtom)
    const [isShowVimeo] = useAtom(isShowVimeoAtom)
    const isClient = useIsClient()
    const ref = useRef<HTMLDivElement>(null)

    // shuffle people in menu
    useEffect(() => {
        if (isOpen || !people || !isClient) return
        const shuffled = [...people].sort(() => Math.random() - 0.5)
        setShufflePeople(shuffled)
    }, [isOpen, people, isClient])

    const handleToggleMenu = () => setIsOpen(!isOpen)
    const hideShowLoading = () => setIsShowLoading(true)

    const handleMenu = () => {
        setIsOpen(!isOpen)
        setTimeout(() => hideShowLoading(), 450)
    }

    useEffect(() => {
        if (!isOpen || !isClient) return
        const handleClickOutside = (event: MouseEvent) => {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                setIsOpen(false)
            }
        }
        if (typeof document !== 'undefined') {
            document.addEventListener('mousedown', handleClickOutside)
            return () =>
                document.removeEventListener('mousedown', handleClickOutside)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, ref, isClient])

    useEffect(() => {
        if (!isOpen || !isClient) return
        if (typeof document !== 'undefined') {
            // body scroll lock
            document.body.style.overflow = 'hidden'

            // fadeout main content if isOpen is true
            const mainContent = document.getElementById('main')
            if (mainContent) {
                isOpen && mainContent.classList.add('opacity-40')
            }

            return () => {
                document.body.style.overflow = ''
                if (mainContent) {
                    mainContent.classList.remove('opacity-40')
                }
            }
        }
    }, [isOpen, isClient])

    const styles =
        'w-[70%] h-[1px] bg-v-navy absolute top-1/2 left-1/2 -translate-x-1/2 transition-all duration-300'

    const ease = cubicBezier(0.42, 0.1, 0.11, 0.99)

    return (
        <MotionConfig transition={{ duration: 0.6, ease }}>
            <div
                ref={ref}
                className={clsx(
                    'fixed top-0 left-0 w-full overflow-hidden transition-opacity duration-300',
                    isShowVimeo ? '-z-1' : 'z-header'
                )}
            >
                <header className="fixed z-header flex justify-between items-center md:items-start px-5 md:pl-[1.8vw] md:pr-[1vw] py-7 md:py-[2.4vh] w-full translate-y-0">
                    <HeaderLogo
                        isHome={isHome}
                        setIsShowLoading={setIsShowLoading}
                    />
                    {!isHome || isShowLoading ? (
                        <MenuButton
                            isOpen={isOpen}
                            handleToggleMenu={handleToggleMenu}
                            styles={styles}
                        />
                    ) : (
                        <CloseLoadingLayerButton
                            hideShowLoading={hideShowLoading}
                            styles={styles}
                        />
                    )}
                </header>

                <Menu
                    people={shufflePeople}
                    isOpen={isOpen}
                    handleMenu={handleMenu}
                    handleToggleMenu={handleToggleMenu}
                />
            </div>
        </MotionConfig>
    )
}
